import type { User } from '@/lib/models';
import { createStore, useStore } from '@/lib/store';

/**
 * The signed-in session.
 *
 * Stored under `currentAccount` in `localStorage`, which is the key RinNET's frontend
 * uses and the shape it stores. That is deliberate and worth keeping: the two portals
 * talk to the same API, so a user who switches between them keeps their session, and
 * the acceptance harness can seed a session by writing one key before any script runs.
 */
export interface Account {
  tokenType: string;
  accessToken: string;
  refreshToken: string;
}

const STORAGE_KEY = 'currentAccount';

function readStored(): Account | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Account | null;
    // A truncated or hand-edited value must not wedge every future load.
    return parsed && typeof parsed.accessToken === 'string' ? parsed : null;
  } catch {
    return null;
  }
}

export const accountStore = createStore<Account | null>(readStored());

/** The cached `/api/user/me`, so the shell and the game pages share one fetch. */
export const userStore = createStore<User | null>(null);

export function getAccount(): Account | null {
  return accountStore.get();
}

export function setAccount(account: Account | null): void {
  try {
    if (account) localStorage.setItem(STORAGE_KEY, JSON.stringify(account));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Private mode denies storage. The session still works for this tab; it just will
    // not survive a reload, which beats refusing to sign in at all.
  }
  accountStore.set(account);
}

export function clearAccount(): void {
  setAccount(null);
  userStore.set(null);
}

export function getCurrentUser(): User | null {
  return userStore.get();
}

/** The stored session, as a hook. Null means signed out. */
export function useAccount(): Account | null {
  return useStore(accountStore);
}

export function useCurrentUser(): User | null {
  return useStore(userStore);
}

/**
 * The card every game page reads by default.
 *
 * Falls back to the first card rather than null: an account whose default flag was
 * never set still has exactly one card in practice, and refusing to pick it would
 * empty every game page for no reason the user can act on.
 */
export function defaultCard(user: User | null): User['cards'][number] | null {
  if (!user || user.cards.length === 0) return null;
  return user.defaultCard ?? user.cards.find((card) => card.default) ?? user.cards[0] ?? null;
}
